// CLI v0.8 — `cloudgrid clone <entity-name-or-id> [target-dir]`.
//
// Spec v17 §13.7. The git-style takeover verb: one command that downloads the
// last-deployed source, writes the entity's `cloudgrid.yaml`, and links the
// folder to the live entity (`.cloudgrid/link.json`) so the very next
// `cloudgrid plug` deploys against the right entity in the right org.
//
// `pull` downloads bytes only — useful for inspection, but a `plug` afterwards
// errors with "cloudgrid.yaml not found" and no link. `clone` is the path for
// taking over an entity someone else created: download + manifest + link, in
// one verb, matching the `git clone` mental model.
//
// `--no-bind` is the escape hatch: behave exactly like `pull` (source bytes
// only, no yaml, no link) when the caller wants the code without the binding.

import { Command as CommanderCommand } from 'commander';
import { existsSync, mkdirSync, readdirSync, readFileSync, writeFileSync } from 'fs';
import { dirname, isAbsolute, join } from 'path';
import { fileURLToPath } from 'url';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg, type ActiveOrg } from '../active-org.js';
import { isGlobalEntityRef } from '../services/entity-ref.js';
import { resolveBareSlugAcrossOrgs, bareSlugNotFoundMessage } from '../services/resolve-bare-slug.js';
import { appendGitignoreLine, writeLink, type LinkArtifact } from '../services/link.js';
import { CloudGridError } from '../utils.js';
import { downloadEntitySource } from './pull.js';

const __dirname = dirname(fileURLToPath(import.meta.url));

/** Resolve the CLI's own published version for the link.json `cli_version`
 *  field. Mirrors plug.ts — walks up from this module looking for the CLI
 *  package.json so the value is correct whether run from src or dist. */
function findPkgVersion(startDir: string): string {
  let dir = startDir;
  for (let i = 0; i < 6; i++) {
    try {
      const pkg = JSON.parse(readFileSync(join(dir, 'package.json'), 'utf-8'));
      if (pkg.name === '@cloudgrid-io/cli' && typeof pkg.version === 'string') {
        return pkg.version;
      }
    } catch {
      // fall through to parent
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return '0.0.0';
}
const PKG_VERSION: string = findPkgVersion(__dirname);

/** Runtime-detail shape we read from `GET /api/v2/runtimes/:idOrName`. Only the
 *  fields clone needs to build the link + address the source/yaml endpoints. */
interface RuntimeDetail {
  entity_id?: string;
  name?: string;
  kind?: 'app' | 'agent';
  url?: string;
  org?: { org_id?: string; slug?: string };
}

interface CloudgridYamlResponse {
  yaml: string;
}

export interface RunCloneOpts {
  client: IV2Client;
  /** Entity slug or id to clone. Required — clone never reads link.json. */
  target: string;
  /** Optional directory to clone into. Defaults to the entity's name. */
  targetDir?: string;
  cwd?: string;
  /** `--org <slug>` — one-shot active-org override (same semantics as plug). */
  org?: string;
  /** Allow cloning into a non-empty directory. */
  force?: boolean;
  /** Download source bytes only — skip yaml + link + gitignore. Like `pull`. */
  noBind?: boolean;
  /** ADR 036 V3 — clone an older version's source + yaml instead of HEAD. */
  version?: string;
}

export async function runClone(opts: RunCloneOpts): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();
  let idOrName = (opts.target ?? '').trim();
  if (idOrName.length === 0) {
    throw new CloudGridError('Provide an entity name or id: gridctl pickup <name>');
  }

  // Entity resolution (ADR 036 V2 + CLI 0.9.3):
  //  - `--org` explicit override → resolve in that active org, as before.
  //  - UUID or `org/slug` target → globally addressable; the server resolves
  //    it by membership in the entity's home org, so no active-org round-trip.
  //  - bare slug, no `--org` → search across every org you belong to and
  //    resolve uniquely, instead of silently scoping to the active org and
  //    failing on a "wrong org" error.
  let activeOrg: ActiveOrg | null = null;
  let client = opts.client;
  if (opts.org !== undefined) {
    activeOrg = await getActiveOrg(opts.client, { override: opts.org });
    client = opts.client.withOrgSlug(activeOrg.slug);
  } else if (!isGlobalEntityRef(idOrName)) {
    const match = await resolveBareSlugAcrossOrgs(idOrName, opts.client);
    if (!match) {
      throw new CloudGridError(bareSlugNotFoundMessage(idOrName, 'clone'));
    }
    // Address the resolved entity by its UUID — globally addressable, so the
    // source/yaml fetches below need no active-org context.
    idOrName = match.entity_id;
  }

  // Resolve the entity. The detail response is the source of truth for the
  // canonical name, kind, and org — exactly what link.json needs. A 404 / 403
  // propagates to the caller's error handler with the server's wording.
  const runtime = await client.get<RuntimeDetail>(
    `/api/v2/runtimes/${encodeURIComponent(idOrName)}`,
  );
  const entityId = runtime?.entity_id;
  if (!entityId) {
    throw new CloudGridError(
      `Server returned no entity_id for ${idOrName}. Report this as a bug.`,
    );
  }
  const name = runtime?.name ?? idOrName;

  // Target directory: explicit arg, else the entity name. Refuse a non-empty
  // directory unless --force, mirroring pull's overwrite guard.
  const dirArg = opts.targetDir ?? name;
  const targetDir = isAbsolute(dirArg) ? dirArg : join(cwd, dirArg);
  preflightCloneDir(targetDir, opts.force ?? false);

  await downloadEntitySource({
    client,
    entityId,
    targetDir,
    force: opts.force ?? false,
    version: opts.version,
  });

  if (opts.noBind) {
    // Escape hatch: bytes only. No yaml, no link — same end state as `pull`.
    process.stdout.write(
      `Downloaded ${name} into ./${relativeName(cwd, targetDir)} (source only, not linked).\n`,
    );
    return;
  }

  // Fetch + write cloudgrid.yaml. A 404 (entity charged but never deployed)
  // is not fatal — clone still links the folder; the user writes the yaml by
  // hand. Any other error propagates.
  let yamlWritten = false;
  try {
    const yamlResp = await client.get<CloudgridYamlResponse>(
      `/api/v2/runtimes/${encodeURIComponent(entityId)}/cloudgrid-yaml`,
      opts.version ? { version: opts.version } : undefined,
    );
    writeFileSync(join(targetDir, 'cloudgrid.yaml'), yamlResp.yaml, 'utf-8');
    yamlWritten = true;
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      process.stderr.write(
        `No cloudgrid.yaml available yet (entity has not deployed). ` +
          `Write one before running cloudgrid plug.\n`,
      );
    } else {
      throw err;
    }
  }

  // Decision 012 §4 names `kind` as required on link.json. A missing or
  // unrecognised value is a server bug, not something to paper over.
  const kind = runtime?.kind;
  if (kind !== 'app' && kind !== 'agent') {
    throw new CloudGridError(
      `Server returned entity ${idOrName} without a valid 'kind' field. ` +
        `Expected 'app' or 'agent'. Report this as a bug.`,
    );
  }

  // The detail response's `org` block is the source of truth; the resolved
  // active org (when present) is only a fallback for the bare-slug path.
  const orgId = runtime?.org?.org_id ?? activeOrg?.org_id;
  const orgSlug = runtime?.org?.slug ?? activeOrg?.slug;
  if (!orgId || !orgSlug) {
    throw new CloudGridError(
      `Server returned no grid for ${idOrName}. Report this as a bug.`,
    );
  }

  const link: LinkArtifact = {
    entity_id: entityId,
    org_id: orgId,
    org_slug: orgSlug,
    kind,
    ...(runtime?.name ? { entity_slug: runtime.name } : {}),
    created_at: new Date().toISOString(),
    cli_version: PKG_VERSION,
  };
  writeLink(link, targetDir);
  appendGitignoreLine('.cloudgrid/', targetDir);

  const relName = relativeName(cwd, targetDir);
  const lines = [`Cloned ${name} into ./${relName}`];
  if (runtime?.url) lines.push(`Public URL: ${runtime.url}`);
  if (!yamlWritten) lines.push('Note: no cloudgrid.yaml yet — write one before plugging.');
  lines.push(`Next: cd ${relName} && cloudgrid plug`);
  process.stdout.write(lines.join('\n') + '\n');
}

/**
 * Refuse to clone into a non-empty directory unless --force. Unlike pull's
 * per-service guard, clone also writes `cloudgrid.yaml` and `.cloudgrid/` at
 * the directory root, so a populated target risks clobbering an existing
 * checkout. Creates the directory when absent.
 */
function preflightCloneDir(targetDir: string, force: boolean): void {
  if (!existsSync(targetDir)) {
    mkdirSync(targetDir, { recursive: true });
    return;
  }
  if (force) return;
  if (readdirSync(targetDir).length > 0) {
    const shown = targetDir.split('/').pop() ?? targetDir;
    throw new V2ApiError(
      409, 'TARGET_NOT_EMPTY',
      `./${shown} already has files. Pass --force to clone into it anyway.`,
    );
  }
}

/** Display name for the target dir relative to cwd, falling back to its
 *  basename when it sits outside cwd (an absolute target-dir arg). */
function relativeName(cwd: string, targetDir: string): string {
  if (targetDir.startsWith(cwd + '/')) return targetDir.slice(cwd.length + 1);
  return targetDir.split('/').pop() ?? targetDir;
}

/**
 * Build the takeover command shared by `pickup` (and, before the BL598 Phase 2
 * clean break, the deleted `clone`): download source + cloudgrid.yaml + link.
 * The `clone` verb itself is gone; this factory remains because `pickup` is
 * built from it. The optional `deprecationLine` parameter is retained as a
 * dormant seam — current callers (pickup) pass none.
 */
export function makeCloneLikeCommand(
  verb: string,
  deprecationLine?: string,
): CommanderCommand {
  return new CommanderCommand(verb)
    .description('Download an entity\'s source + cloudgrid.yaml and link the folder to it.')
    .argument('<name>', 'Entity slug or id to pick up')
    .argument('[target-dir]', 'Directory to pick up into (defaults to the entity name)')
    .option('--org <slug>', 'Grid to resolve the entity in (defaults to the active grid)')
    .option('--force', 'Pick up into a non-empty directory')
    .option('--version <id>', 'Pick up an older version\'s source + cloudgrid.yaml instead of HEAD')
    .option('--no-bind', 'Download source only — skip cloudgrid.yaml and the link')
    .action(
      async (
        name: string,
        targetDir: string | undefined,
        opts: { org?: string; force?: boolean; bind?: boolean; version?: string },
      ) => {
        if (deprecationLine) process.stderr.write(deprecationLine + '\n');
        try {
          const client = await getV2Client(opts.org);
          await runClone({
            client,
            target: name,
            targetDir,
            org: opts.org,
            force: opts.force,
            version: opts.version,
            // Commander maps `--no-bind` to `opts.bind === false`.
            noBind: opts.bind === false,
          });
        } catch (err) {
          await handleV2Error(err);
        }
      },
    );
}
